import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { getIconComponent } from "@/utils/iconMapper";
import { Loader2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";

const Services = () => {
  const [services, setServices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadServices();

    // Subscribe to realtime changes with error handling
    const channel = supabase
      .channel('services-page-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'services'
        },
        (payload) => {
          console.log('Services page changed:', payload);
          loadServices();
        }
      )
      .subscribe((status, err) => {
        // Handle subscription errors gracefully without logging to console
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          // Silently handle connection errors - realtime is non-critical
          return;
        }
      });

    // Cleanup subscription on unmount
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const loadServices = async () => {
    try {
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      setServices(data || []);
    } catch (error) {
      console.error('Error loading services:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Cyber Essentials Certification & Security Services UK | 4FOX Security"
        description="Get Cyber Essentials certified with UK's trusted security partner. We offer Cyber Essentials, Cyber Essentials Plus, fractional CISO, risk management, and incident response services. 98% pass rate."
        canonical="https://4fox.uk/services"
        keywords={["Cyber Essentials certification", "Cyber Essentials Plus UK", "cyber security services UK", "fractional CISO", "IASME certification body", "UK cyber security consultancy"]}
        schema={{
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": "Cyber Security Consulting",
          "provider": {
            "@type": "Organisation",
            "name": "4FOX Security",
            "url": "https://4fox.uk"
          },
          "areaServed": {
            "@type": "Country",
            "name": "United Kingdom"
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Cyber Security Services",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Cyber Essentials Certification",
                  "description": "UK Government-backed cyber security certification"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Fractional CISO",
                  "description": "Part-time Chief Information Security Officer services"
                }
              }
            ]
          }
        }}
      />
      <Navbar />
      
      <main className="pt-24 pb-16 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Cyber Essentials & Security Services
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              From Cyber Essentials certification to strategic fractional CISO support, 
              we provide comprehensive cyber security services for UK businesses.
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {services.map((service) => {
                const Icon = getIconComponent(service.icon_name);
                return (
                  <Link key={service.id} to={service.url}>
                    <Card 
                      className="group transition-all duration-300 hover:shadow-lg hover:-translate-y-1 hover:border-accent hover:border-2 cursor-pointer h-full"
                    >
                      <CardHeader>
                        <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 bg-muted text-muted-foreground group-hover:bg-accent group-hover:text-white transition-colors duration-300">
                          <Icon className="w-6 h-6" />
                        </div>
                        <CardTitle className="text-xl">{service.title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <CardDescription className="text-base">
                          {service.description}
                        </CardDescription>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Services;
